import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCustomerFleetDevices, setCustomerFleetDeviceCan } from '../redux/adminSlice';
import { normalizeTenantModules } from './tenantModulesCatalog';

// Devices tab (fleet CAN-odometer round, 2026-09-24). Lists the platform's
// `fleetDevices/{imei}` rows for ONE customer (root allowlist, mirrored to
// `customers/{cid}/fleetDevices/{imei}`) and lets the admin flip the
// per-device `canOdometer` flag - whether THAT specific tracker has a CAN
// bus (a skid steer/trailer tracker is GPS-only). `web.fleetCan` (Modules
// tab) is the separate BILLING entitlement; a device switch here is saved
// regardless of it, it just isn't USED for odometer math until both are on
// (see the amber note below) - see resolveOdometerMode/canEnabled in
// my-app/functions/allFunctions/shared/odometerHelpers.js.
//
// `customerId` is the only identity this panel trusts: the redux slice keys
// `fleetDevicesByCustomer` by it, and every render below reads/writes only
// that key - never a global/shared list - so this panel can never leak one
// tenant's devices into another's drawer.
const CustomerFleetDevicesPanel = ({ customer }) => {
  const dispatch = useDispatch();
  const customerId = customer?.id;

  // Raw (possibly undefined) redux bucket - undefined means "never fetched
  // for this customer yet", which is what drives the one-time fetch below.
  // Deliberately not using a defaulting selector here: a defaulted {items:
  // [], loading:false} would be indistinguishable from "fetched and empty".
  const bucket = useSelector((state) => state.admin.fleetDevicesByCustomer[customerId]);
  const items = bucket?.items || [];
  const loading = bucket?.loading || false;
  const fetchError = bucket?.error || null;

  const [savingImei, setSavingImei] = useState(null);
  const [toggleError, setToggleError] = useState(null);
  const savingRef = useRef(null); // sync guard: mirrors savingImei without the render lag

  useEffect(() => {
    if (!customerId) {
      return;
    }
    // "On first open of the tab for this customer" - fires once per
    // customer (the bucket exists after that, fetched or errored, so this
    // never re-fires just from switching tabs away and back).
    if (bucket === undefined) {
      dispatch(fetchCustomerFleetDevices(customerId));
    }
  }, [customerId, bucket, dispatch]);

  const canModuleEnabled = normalizeTenantModules(customer?.modules).web.fleetCan;

  // Refresh/retry (review B1): the effect above fetches once per customer,
  // so a failed load or a device the owner registered later needs this.
  const handleRefresh = () => {
    if (!customerId || loading) {
      return;
    }
    dispatch(fetchCustomerFleetDevices(customerId));
  };

  const handleToggle = async (device) => {
    if (savingRef.current) {
      return; // sync guard: a double-click on any switch while one save is
      // already in flight can never double-submit
    }
    savingRef.current = device.imei;
    setSavingImei(device.imei);
    setToggleError(null);
    try {
      await dispatch(
        setCustomerFleetDeviceCan({
          customerId,
          imei: device.imei,
          canOdometer: !device.canOdometer,
        })
      ).unwrap();
    } catch (err) {
      setToggleError(
        typeof err === 'string' ? err : err?.message || 'Failed to update device. Please try again.'
      );
    } finally {
      savingRef.current = null;
      setSavingImei(null);
    }
  };

  return (
    <div className="rounded-xl border border-[rgba(150,165,180,.25)] bg-white p-6 shadow-[0_1px_3px_rgba(29,43,51,.08)]">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h4 className="text-[11px] font-bold uppercase tracking-[0.08em] text-[#64798a]">Devices</h4>
        <button
          type="button"
          onClick={handleRefresh}
          disabled={loading || !customerId}
          className="text-xs font-semibold text-[#2f66b8] hover:underline disabled:cursor-not-allowed disabled:opacity-50"
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>
      <p className="text-sm text-[#64798a]">
        GPS trackers registered to this company&rsquo;s vehicles and equipment. Turn on CAN odometer only for a
        device that is actually wired to the vehicle&rsquo;s CAN bus.
      </p>

      {!canModuleEnabled && (
        <p className="mt-3 rounded-lg border border-[#e0a23c] bg-[rgba(224,162,60,.1)] p-3 text-xs font-semibold text-[#8a6420]">
          CAN odometer (premium) is off for this company - device switches are saved but not used until it is
          enabled in Modules.
        </p>
      )}

      <div className="mt-4">
        {loading ? (
          <p className="text-sm text-[#64798a]">Loading devices…</p>
        ) : fetchError ? (
          <p className="text-sm font-semibold text-[#d96a5b]">{fetchError}</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-[#64798a]">No GPS devices registered.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-[#1d2b33]">
              <thead>
                <tr>
                  <th className="border-b border-[rgba(150,165,180,.25)] p-2 text-[11px] font-bold uppercase tracking-[0.08em] text-[#64798a]">
                    Asset
                  </th>
                  <th className="border-b border-[rgba(150,165,180,.25)] p-2 text-[11px] font-bold uppercase tracking-[0.08em] text-[#64798a]">
                    Type
                  </th>
                  <th className="border-b border-[rgba(150,165,180,.25)] p-2 text-[11px] font-bold uppercase tracking-[0.08em] text-[#64798a]">
                    IMEI
                  </th>
                  <th className="border-b border-[rgba(150,165,180,.25)] p-2 text-[11px] font-bold uppercase tracking-[0.08em] text-[#64798a]">
                    Status
                  </th>
                  <th className="border-b border-[rgba(150,165,180,.25)] p-2 text-[11px] font-bold uppercase tracking-[0.08em] text-[#64798a]">
                    CAN odometer
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((device) => {
                  const isSaving = savingImei === device.imei;
                  const checked = device.canOdometer === true;
                  return (
                    <tr key={device.imei} className="border-b border-[rgba(150,165,180,.15)]">
                      <td className="p-2">
                        <div>{device.assetName || <span className="text-[#93a4b3]">—</span>}</div>
                        {device.label && <div className="text-xs text-[#64798a]">{device.label}</div>}
                      </td>
                      <td className="p-2 text-[#1d2b33]">
                        {device.assetType === 'equipment' ? 'Equipment' : 'Vehicle'}
                      </td>
                      <td className="p-2 font-mono text-xs text-[#1d2b33]">{device.imei}</td>
                      <td className="p-2">
                        <span
                          className="inline-flex items-center gap-1.5"
                          title={device.active ? 'Active' : 'Inactive'}
                        >
                          <span
                            className="h-2 w-2 rounded-full"
                            style={{ backgroundColor: device.active ? '#2ea183' : '#96a5b4' }}
                          />
                          {device.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="p-2">
                        <button
                          type="button"
                          role="switch"
                          aria-checked={checked}
                          aria-label={`CAN odometer for ${device.assetName || device.imei}`}
                          disabled={savingImei !== null}
                          onClick={() => handleToggle(device)}
                          className={
                            (checked ? 'bg-[#2f66b8]' : 'bg-[rgba(150,165,180,.4)]') +
                            ' relative inline-flex h-5 w-9 flex-none items-center rounded-full transition disabled:cursor-not-allowed disabled:opacity-50'
                          }
                        >
                          <span
                            className={
                              (checked ? 'translate-x-4' : 'translate-x-0.5') +
                              ' inline-block h-4 w-4 transform rounded-full bg-white transition'
                            }
                          />
                        </button>
                        {isSaving && <span className="ml-2 text-xs text-[#64798a]">…</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {toggleError && <p className="mt-3 text-sm font-semibold text-[#d96a5b]">{toggleError}</p>}

      <p className="mt-3 text-xs text-[#64798a]">GPS odometer is used for every device without CAN.</p>
    </div>
  );
};

export default CustomerFleetDevicesPanel;
